import React from 'react';
import { CreateRouteMapProps } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useGetOrderQuery } from '@src/features/order/redux';
import { OrderIdParams } from '@src/core/entities';
import { skipToken } from '@reduxjs/toolkit/query';
import { selectDrone, setWaypoints } from '@src/features/order/pages/upsert-mission-page/redux';
import { RouteWPS, TerrainAware } from '@src/features/order/pages/upsert-mission-page/components';
import { RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import { ArcStack } from '@src/core/ui';
import { DroneStatusType, OrderEntity, SettingUnitType, useCheckAndPlanMutation, useDroneProvider, useGetSettingByUnitType } from '@src/core/features';
import { ArcDroneMarker } from '@src/core/controllers';
import { arcConvertBetweenUnitTypes } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { getUnitType, useUnitType } from '@src/core/features/settings/units';
import { UnitType } from '@src/core/enums';
import { trackError, toTrackedError } from '@src/core/sentry';

const CreateRouteMap: React.FC<CreateRouteMapProps> = (props) => {
  const { orderId: propOrderId, selectDroneSelector, useOrderQueryHook } = props;
  const dispatch = useDispatch();
  const params = useParams<OrderIdParams>();
  const effectiveOrderId = propOrderId || params.orderId;
  
  const selected = selectDroneSelector 
    ? useSelector(selectDroneSelector)
    : useSelector(selectDrone);
  
  const orderQuery = useOrderQueryHook && effectiveOrderId
    ? useOrderQueryHook(effectiveOrderId, { skip: !effectiveOrderId })
    : useGetOrderQuery(effectiveOrderId ? effectiveOrderId : skipToken);
  const { data, isLoading } = orderQuery;
  
  const [checkAndPlan, { isLoading: isChecking }] = useCheckAndPlanMutation();
  const { drones: idleDrones } = useDroneProvider(DroneStatusType.IDLE);

  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const unitType = useUnitType();
  // FLIGHT_ALTITUDE setting now arrives in SI (m). Waypoint altitudes are stored/edited
  // in the user's display unit (see EnterRouteForm) and submitted with unitSystem, so
  // convert SI -> display before seeding waypoints.
  const flightAltitudeDisplay = React.useMemo(
    () => arcConvertBetweenUnitTypes(Number(flightAltitude.value), MeasurementType.DISTANCE, UnitType.SI, getUnitType()),
    [flightAltitude.value, unitType],
  );

  const droneLat = Number(selected?.latitude);
  const droneLng = Number(selected?.longitude);

  const destinationLat = Number(data?.destinationLocation?.latitude);
  const destinationLng = Number(data?.destinationLocation?.longitude);


  const onCheckAndPlan = (order: OrderEntity) => {
    const payload = [
      { latitude: droneLat,  longitude: droneLng },
      { orderId: order.id, latitude: destinationLat,  longitude: destinationLng },
      { latitude: droneLat,  longitude: droneLng },
    ];

    checkAndPlan({ path: payload }).unwrap().then((response) => {
      const wps = response.map((wp, idx) => {
        const updatedWp: RouteWaypointEntity = {
          uuid: uuidv4(),
          orderId: wp.orderId,
          latitude: wp.latitude,
          longitude: wp.longitude,
          type: RouteWaypointType.WAYPOINT,
          altitude: flightAltitudeDisplay,
        };
        if (idx === 0 || idx === response.length - 1) {
          return {
            ...updatedWp,
            type: RouteWaypointType.TAKE_OFF,
          };
        }
        if (wp.orderId) {
          return {
            ...updatedWp,
            type: RouteWaypointType.DESTINATION,
          };
        }
        return updatedWp;
      });
      dispatch(setWaypoints(wps));
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'checkAndPlan' } });
    });
  };


  React.useEffect(() => {
    if (!data || !flightAltitude.value) return;
    onCheckAndPlan(data);
  }, [data, flightAltitude]);

  if (isLoading || isChecking) return null;

  return (
    <React.Fragment>
      {idleDrones.map((drone) => {
        if (drone.id === selected?.id) {
          return (
            <ArcDroneMarker.idleSelected 
              key={drone.id} 
              position={{ 
                lat: Number(drone.latitude), 
                lng: Number(drone.longitude),
              }} 
            />
          );
        }
        return ( 
          <ArcDroneMarker.idleArrow 
            key={drone.id}
            position={{ 
              lat: Number(drone.latitude), 
              lng: Number(drone.longitude),
            }}  
          />
        );
      })}
      <RouteWPS />
      <ArcStack
        p={1}
        width='100%' 
        height={300} 
        zIndex={100}
        bottom={0}
        bgcolor='transparent'
        position='absolute'>
        <TerrainAware />
      </ArcStack>
    </React.Fragment>
  );
};

export { CreateRouteMap };
export default CreateRouteMap;